import { useState } from 'react'
import { supabase } from '../lib/supabase'

/**
 * Pantalla de autenticación.
 *
 * Modos:
 *   - login            → email + password
 *   - register         → alta manual (poco usado, admin invita normalmente)
 *   - forgot           → envía email de reset de password
 *   - update_password  → formulario para establecer nueva contraseña. Se
 *                        muestra cuando el usuario ha llegado por un link
 *                        de invitación o de recuperación (App.js lo fuerza
 *                        con la prop forceMode).
 *
 * Sin el modo update_password, un usuario invitado o que hace reset entra
 * automáticamente por el link del email pero SIN establecer contraseña
 * real. Ese era el bug que impedía a Marta y Silva loguearse con
 * credenciales normales tras el primer acceso.
 */
export default function AuthScreen({ forceMode = null, onPasswordSet = null }) {
  const [mode, setMode] = useState(forceMode || 'login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [error, setError] = useState(null)

  const handleLogin = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) setError('Email o contraseña incorrectos')
    setLoading(false)
  }

  const handleRegister = async (e) => {
    e.preventDefault()
    if (password.length < 6) { setError('La contraseña debe tener al menos 6 caracteres'); return }
    setLoading(true)
    setError(null)
    const { error } = await supabase.auth.signUp({ email, password })
    if (error) setError(error.message)
    else setMessage('Cuenta creada. Revisa tu email para confirmar.')
    setLoading(false)
  }

  const handleForgot = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin
    })
    if (error) setError(error.message)
    else setMessage('Email enviado. Revisa tu bandeja de entrada.')
    setLoading(false)
  }

  const handleUpdatePassword = async (e) => {
    e.preventDefault()
    setError(null)
    setMessage(null)
    if (password.length < 6) { setError('La contraseña debe tener al menos 6 caracteres'); return }
    if (password !== passwordConfirm) { setError('Las contraseñas no coinciden'); return }
    setLoading(true)
    // Guardamos también el flag password_set en user_metadata para que App.js
    // sepa que este usuario ya estableció su contraseña. Es la fuente de
    // verdad (más robusta que depender del hash de la URL, que puede haberse
    // consumido antes de tiempo por Supabase-js).
    const { error } = await supabase.auth.updateUser({
      password,
      data: { password_set: true },
    })
    if (error) {
      setError(error.message)
      setLoading(false)
      return
    }
    // Éxito: limpiamos el hash de la URL (llevaba el token de recovery/invite)
    // y avisamos al padre para que App.js reevalúe el estado y renderice
    // la app en vez de esta pantalla.
    setMessage('Contraseña establecida. Entrando a la aplicación...')
    if (typeof window !== 'undefined' && window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname)
    }
    setTimeout(() => {
      if (onPasswordSet) onPasswordSet()
    }, 800)
    setLoading(false)
  }

  const formHandler =
    mode === 'login'           ? handleLogin :
    mode === 'register'        ? handleRegister :
    mode === 'forgot'          ? handleForgot :
    mode === 'update_password' ? handleUpdatePassword :
    handleLogin

  const isUpdatePassword = mode === 'update_password'
  const titleByMode = {
    login:            'Iniciar sesión',
    register:         'Crear cuenta',
    forgot:           'Recuperar contraseña',
    update_password:  'Establece tu contraseña',
  }
  const buttonLabelByMode = {
    login:            'Entrar',
    register:         'Crear cuenta',
    forgot:           'Enviar email',
    update_password:  'Guardar contraseña',
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#F5F0EA',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'DM Sans', sans-serif",
      padding: '1rem'
    }}>
      <style>{`
        .auth-input {
          width: 100%;
          padding: 0.75rem 1rem;
          background: #FFFFFF;
          border: 1px solid #DDD5C8;
          border-radius: 4px;
          color: #2C2017;
          font-size: 0.9rem;
          font-family: inherit;
          transition: border-color 0.2s, box-shadow 0.2s;
          outline: none;
        }
        .auth-input:focus {
          border-color: #5C4A3A;
          box-shadow: 0 0 0 3px rgba(59,47,47,0.08);
        }
        .auth-input::placeholder { color: #B0A89E; }
        .auth-btn {
          width: 100%;
          padding: 0.75rem;
          background: #3B2F2F;
          color: #F5F0EA;
          border: none;
          border-radius: 4px;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
          font-family: inherit;
          letter-spacing: 0.01em;
        }
        .auth-btn:hover:not(:disabled) { background: #5C4A3A; }
        .auth-btn:disabled { opacity: 0.55; cursor: not-allowed; }
        .link-btn {
          background: none;
          border: none;
          color: #5C4A3A;
          cursor: pointer;
          font-size: 0.875rem;
          text-decoration: underline;
          font-family: inherit;
          font-weight: 500;
        }
        .link-btn:hover { color: #3B2F2F; }
      `}</style>

      <div style={{ width: '100%', maxWidth: '400px' }}>

        {/* Bloque unificado: cabecera marrón con logo + título + formulario */}
        <div style={{
          border: '1px solid #DDD5C8',
          borderRadius: '10px',
          overflow: 'hidden',
          boxShadow: '0 4px 16px rgba(59,47,47,0.08)',
          background: '#FFFFFF',
        }}>
          {/* Cabecera oscura con logo HAUS */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#3B2F2F',
            padding: '2rem',
          }}>
            <img
              src="/logo-haus.png"
              alt="HAUS Healthy Buildings"
              style={{
                height: '58px',
                width: 'auto',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          </div>

          {/* Franja del nombre del producto */}
          <div style={{
            background: '#F5F0EA',
            borderBottom: '1px solid #DDD5C8',
            padding: '1.1rem 1.5rem',
            textAlign: 'center',
          }}>
            <h1 style={{
              fontSize: '1.35rem',
              fontWeight: '600',
              color: '#2C2017',
              margin: 0,
              letterSpacing: '0.04em',
            }}>BREEAM MONITOR</h1>
          </div>

          {/* Formulario */}
          <div style={{ padding: '2rem' }}>
          <h2 style={{
            color: '#2C2017',
            fontSize: '1.05rem',
            fontWeight: '600',
            marginBottom: isUpdatePassword ? '0.5rem' : '1.5rem',
            textAlign: 'center'
          }}>
            {titleByMode[mode]}
          </h2>

          {isUpdatePassword && (
            <p style={{ color: '#7A6A5A', fontSize: '0.825rem', textAlign: 'center', marginBottom: '1.5rem', lineHeight: 1.5 }}>
              Estás accediendo por un enlace de invitación o recuperación.
              Elige una contraseña para poder entrar directamente la próxima vez.
            </p>
          )}

          {error && (
            <div style={{
              background: '#FDF2F0',
              border: '1px solid #D4614A',
              borderLeft: '3px solid #D4614A',
              borderRadius: '4px',
              padding: '0.75rem 1rem',
              marginBottom: '1rem',
              color: '#8B2A1A',
              fontSize: '0.85rem'
            }}>
              {error}
            </div>
          )}
          {message && (
            <div style={{
              background: '#F5FAF0',
              border: '1px solid #8D9A2E',
              borderLeft: '3px solid #8D9A2E',
              borderRadius: '4px',
              padding: '0.75rem 1rem',
              marginBottom: '1rem',
              color: '#4A5A10',
              fontSize: '0.85rem'
            }}>
              {message}
            </div>
          )}

          <form onSubmit={formHandler}>
            {!isUpdatePassword && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={{
                  color: '#2C2017',
                  fontSize: '0.825rem',
                  fontWeight: '500',
                  display: 'block',
                  marginBottom: '0.4rem'
                }}>Email</label>
                <input
                  type="email"
                  className="auth-input"
                  placeholder="correo@empresa.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                />
              </div>
            )}

            {mode !== 'forgot' && (
              <div style={{ marginBottom: isUpdatePassword ? '1rem' : '1.5rem' }}>
                <label style={{
                  color: '#2C2017',
                  fontSize: '0.825rem',
                  fontWeight: '500',
                  display: 'block',
                  marginBottom: '0.4rem'
                }}>{isUpdatePassword ? 'Nueva contraseña' : 'Contraseña'}</label>
                <input
                  type="password"
                  className="auth-input"
                  placeholder={mode === 'register' || isUpdatePassword ? 'Mínimo 6 caracteres' : '••••••••'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  autoComplete={isUpdatePassword ? 'new-password' : 'current-password'}
                />
              </div>
            )}

            {isUpdatePassword && (
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  color: '#2C2017',
                  fontSize: '0.825rem',
                  fontWeight: '500',
                  display: 'block',
                  marginBottom: '0.4rem'
                }}>Repite la contraseña</label>
                <input
                  type="password"
                  className="auth-input"
                  placeholder="Confirma tu contraseña"
                  value={passwordConfirm}
                  onChange={e => setPasswordConfirm(e.target.value)}
                  required
                  autoComplete="new-password"
                />
              </div>
            )}

            <button type="submit" className="auth-btn" disabled={loading}>
              {loading ? 'Procesando...' : buttonLabelByMode[mode]}
            </button>
          </form>

          {/* Links de navegación entre modos — se ocultan en update_password
              porque en ese flujo el usuario TIENE que establecer la contraseña
              antes de poder hacer cualquier otra cosa. */}
          {!isUpdatePassword && (
            <div style={{ marginTop: '1.5rem', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
              {mode === 'login' && <>
                <button className="link-btn" onClick={() => { setMode('forgot'); setError(null); setMessage(null) }}>
                  ¿Olvidaste tu contraseña?
                </button>
                <div style={{ color: '#7A6A5A', fontSize: '0.875rem' }}>
                  ¿No tienes cuenta?{' '}
                  <button className="link-btn" onClick={() => { setMode('register'); setError(null); setMessage(null) }}>
                    Regístrate
                  </button>
                </div>
              </>}
              {mode === 'register' && (
                <div style={{ color: '#7A6A5A', fontSize: '0.875rem' }}>
                  ¿Ya tienes cuenta?{' '}
                  <button className="link-btn" onClick={() => { setMode('login'); setError(null); setMessage(null) }}>
                    Inicia sesión
                  </button>
                </div>
              )}
              {mode === 'forgot' && (
                <button className="link-btn" onClick={() => { setMode('login'); setError(null); setMessage(null) }}>
                  Volver al inicio de sesión
                </button>
              )}
            </div>
          )}
          </div>
        </div>

        <p style={{ color: '#B0A89E', fontSize: '0.75rem', textAlign: 'center', marginTop: '1.25rem' }}>
          Datos protegidos y cifrados con Supabase
        </p>
      </div>
    </div>
  )
}
